import {useEffect, useId, useMemo, useRef, useState} from 'react';
import {
  Box,
  Button,
  HStack,
  IconButton,
  Text,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import {motion, LayoutGroup} from 'framer-motion';
import {TablerX} from '../../icons';
import usePlannerStyles from '../planner/usePlannerStyles';
import {
  LANGUAGE_OPTIONS,
  normalizeTypeaheadText,
} from '../planner/createSessionFormUtils';
import {plannerLanguageMarker} from '../planner/PatientBrowserCard';
import FavoriteStar from './FavoriteStar';
import useLanguageFavorites from '../../hooks/useLanguageFavorites';
import {partitionByFavorite} from '../../common/languageFavoritesOrder';

const MotionBox = motion(Box);
import {
  SearchSelectionInput,
  ViewportAwareDropdownMenu,
  handleDropdownInputKeyDown,
  handleDropdownOptionKeyDown,
} from '../planner/CreateSessionFormSections';

interface LanguageMultiSelectProps {
  value: string[];
  onChange: (languages: string[]) => void;
  placeholder?: string;
  ariaLabel?: string;
}

// Searchable multi-select for languages, built on the same combobox input and
// dropdown primitives the new-encounter form uses, so the look and keyboard
// behavior stay in sync. Selected languages render as removable chips above the
// search field.
export default function LanguageMultiSelect({
  value,
  onChange,
  placeholder = 'Search languages...',
  ariaLabel = 'Search languages',
}: LanguageMultiSelectProps) {
  const styles = usePlannerStyles();
  const {favoriteSet, isFavorite, toggleFavorite} = useLanguageFavorites();
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const reactId = useId().replace(/:/g, '');
  const menuId = `language-multiselect-results-${reactId}`;

  // The dropdown is portaled (fixed positioning) so it escapes the surrounding
  // modal/scroll-container clipping. That means clicks land outside the anchor,
  // so close on an outside pointer-down instead of the input's blur.
  useEffect(() => {
    if (!isOpen) return undefined;
    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (anchorRef.current?.contains(target)) return;
      if (document.getElementById(menuId)?.contains(target)) return;
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [isOpen, menuId]);

  // When the picker opens, bring the field into view (it's often the last field
  // in a scrollable modal) so the results aren't hidden below the fold.
  useEffect(() => {
    if (!isOpen) return;
    anchorRef.current?.scrollIntoView({block: 'center', behavior: 'smooth'});
  }, [isOpen]);

  const filteredOptions = useMemo(() => {
    const query = normalizeTypeaheadText(search);
    return LANGUAGE_OPTIONS.filter(option => {
      if (value.includes(option.language)) return false;
      if (!query) return true;
      return option.searchText.includes(query);
    });
  }, [search, value]);

  const addLanguage = (language: string) => {
    if (!value.includes(language)) onChange([...value, language]);
    setSearch('');
    setIsOpen(true);
    // Adding a chip grows the field, shifting the input down. Nudge the portaled
    // dropdown to re-measure its anchor on the next frame so it stays aligned.
    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(() =>
        window.dispatchEvent(new Event('resize')),
      );
    }
  };

  const removeLanguage = (language: string) => {
    onChange(value.filter(item => item !== language));
  };

  const {favorites: favoriteOptions, others: otherOptions} =
    partitionByFavorite(
      filteredOptions,
      option => option.language,
      favoriteSet,
    );

  const renderGroupLabel = (label: string, key: string) => (
    <MotionBox key={key} layout>
      <Text
        fontSize="xs"
        fontWeight="normal"
        color={styles.subtleText}
        textTransform="uppercase"
        letterSpacing="wider"
        px={3}
        pt={1}
        pb={1.5}
      >
        {label}
      </Text>
    </MotionBox>
  );

  // The `layout` wrapper is what slides a row up into the favorites group (or
  // back down) when its star is toggled, instead of the list snapping.
  const renderOption = (option: (typeof filteredOptions)[number]) => {
    const marker = plannerLanguageMarker(option.language);
    return (
      <MotionBox key={option.id} layout w="100%">
        <Button
          type="button"
          className="item"
          role="option"
          aria-selected={false}
          aria-label={`Add ${option.label}`}
          variant="ghost"
          justifyContent="space-between"
          w="100%"
          h="auto"
          minH="48px"
          py={2.5}
          px={3}
          borderRadius="lg"
          // Keep input focus on mousedown so the surrounding onBlur doesn't
          // close the menu before the click registers (Safari).
          onMouseDown={event => event.preventDefault()}
          onClick={() => addLanguage(option.language)}
          onKeyDown={event =>
            handleDropdownOptionKeyDown(
              event,
              `input[aria-label="${ariaLabel}"]`,
            )
          }
          _hover={{bg: styles.typeaheadHoverBg}}
          _focus={{
            bg: styles.typeaheadHoverBg,
            boxShadow: 'none',
            outline: 'none',
          }}
          _focusVisible={{
            bg: styles.typeaheadHoverBg,
            boxShadow: 'none',
            outline: 'none',
          }}
        >
          <HStack
            spacing={2.5}
            minW={0}
            flex="1 1 auto"
            justify="flex-start"
            overflow="hidden"
          >
            <Text
              as="span"
              aria-hidden="true"
              color={styles.textColor}
              fontSize="md"
              lineHeight="1"
              minW="1.625rem"
              textAlign="center"
            >
              {marker || '\u00A0'}
            </Text>
            <Text
              color={styles.textColor}
              fontWeight="normal"
              fontSize="md"
              noOfLines={1}
              minW={0}
            >
              {option.label}
            </Text>
          </HStack>
          <HStack spacing={2} flexShrink={0}>
            {option.isCommon && (
              <Text
                fontSize="sm"
                bg={styles.commonBadgeBg}
                color={styles.commonBadgeColor}
                px={2}
                py={0.5}
                borderRadius="full"
                fontWeight="normal"
              >
                Common
              </Text>
            )}
            <FavoriteStar
              isFavorite={isFavorite(option.language)}
              onToggle={() => toggleFavorite(option.language)}
              language={option.label}
            />
          </HStack>
        </Button>
      </MotionBox>
    );
  };

  return (
    <Box>
      {value.length > 0 && (
        <Wrap spacing={2} mb={3}>
          {value.map(language => {
            const marker = plannerLanguageMarker(language);
            return (
              <WrapItem key={language}>
                <HStack
                  spacing={1.5}
                  bg={styles.listRowSelectedBg}
                  borderWidth="1px"
                  borderColor={styles.listRowSelectedBorderColor}
                  borderRadius="lg"
                  pl={3}
                  pr={1}
                  h="36px"
                >
                  {marker && (
                    <Text
                      as="span"
                      aria-hidden="true"
                      fontSize="md"
                      lineHeight="1"
                    >
                      {marker}
                    </Text>
                  )}
                  <Text fontSize="md" color={styles.textColor}>
                    {language}
                  </Text>
                  <IconButton
                    aria-label={`Remove ${language}`}
                    icon={<TablerX size={14} />}
                    size="xs"
                    variant="ghost"
                    borderRadius="full"
                    minW="28px"
                    h="28px"
                    color={styles.subtleText}
                    onClick={() => removeLanguage(language)}
                  />
                </HStack>
              </WrapItem>
            );
          })}
        </Wrap>
      )}

      <Box ref={anchorRef} position="relative">
        <SearchSelectionInput
          ariaControls={menuId}
          ariaLabel={ariaLabel}
          isOpen={isOpen}
          onChange={(event: any) => {
            setSearch(event.target.value);
            setIsOpen(true);
          }}
          onClick={() => setIsOpen(true)}
          onFocus={() => setIsOpen(true)}
          onKeyDown={(event: any) =>
            handleDropdownInputKeyDown({
              event,
              menuId,
              onOpen: () => setIsOpen(true),
            })
          }
          placeholder={placeholder}
          value={search}
        />

        {isOpen && (
          <ViewportAwareDropdownMenu
            anchorRef={anchorRef}
            id={menuId}
            className="menu"
            zIndex={1400}
            bg={styles.surfaceBg}
            borderWidth="1px"
            borderColor={styles.borderColor}
            borderRadius="lg"
            boxShadow="lg"
            maxHeightPx={260}
            role="listbox"
            aria-label="Language results"
            p={2}
          >
            {filteredOptions.length > 0 ? (
              <LayoutGroup>
                {favoriteOptions.length > 0 &&
                  renderGroupLabel('Favorites', 'language-group-favorites')}
                {favoriteOptions.map(renderOption)}
                {favoriteOptions.length > 0 &&
                  otherOptions.length > 0 &&
                  renderGroupLabel('All languages', 'language-group-all')}
                {otherOptions.map(renderOption)}
              </LayoutGroup>
            ) : (
              <Text color={styles.subtleText} fontSize="sm" px={3} py={3}>
                {value.length > 0 && !search
                  ? 'All languages added.'
                  : 'No language matches.'}
              </Text>
            )}
          </ViewportAwareDropdownMenu>
        )}
      </Box>
    </Box>
  );
}
